import type {
  SubCategoryItem,
  TabItem,
} from '@/components/section/hero-with-tabs/hero-tab/hero-tab';
import { getProductCatalogue } from '@/lib/contentful/getProductCatalogue';
import {
  getProductCategories as fetchProductCategories,
  getSubcategoriesByParent,
} from '@/lib/contentful/getProductCategories';
import contentfulClient from '@/lib/services/contentful';
import { notFound, redirect } from 'next/navigation';
import { isTypeHeroSection, TypeHeroSection } from 'types/contentful/TypeHeroSection';
import { TypePageSkeleton } from 'types/contentful/TypePage';

/**
 * URL generation utilities for pricing pages
 */
export const generatePricingUrls = {
  /**
   * Generate URL for a category tab (with first subcategory if available)
   */
  getCategoryUrl: (categorySlug: string, tabs: TabItem[]): string => {
    const tab = tabs.find((t) => t.slug === categorySlug);
    if (tab?.subCategories && tab.subCategories.length > 0) {
      return `/pricing/${categorySlug}/${tab.subCategories[0].slug}`;
    }
    return `/pricing/${categorySlug}`;
  },

  /**
   * Generate URL for a subcategory within a category
   */
  getSubcategoryUrl: (categorySlug: string, subcategorySlug: string): string => {
    return `/pricing/${categorySlug}/${subcategorySlug}`;
  },

  /**
   * Generate URL for a pricing band within a subcategory
   */
  getBandUrl: (categorySlug: string, subcategorySlug: string, bandId: string): string => {
    return `/pricing/${categorySlug}/${subcategorySlug}?band=${bandId}`;
  },
};

/**
 * Normalise and validate pricing band query params
 */
export const normaliseBandParam = (band: string | string[] | undefined) => {
  const value = Array.isArray(band) ? band[0] : band;
  if (!value) return undefined;
  return /^\d+$/.test(value) ? value : undefined;
};

/**
 * Validation utilities for pricing pages
 */
export const validatePricingParams = {
  /**
   * Validate that a category exists in the tabs
   */
  validateCategory: (categorySlug: string, tabs: TabItem[]): TabItem | null => {
    return tabs.find((tab) => tab.slug === categorySlug) || null;
  },

  validateSubcategory: (
    subcategorySlug: string,
    subCategories: SubCategoryItem[],
  ): SubCategoryItem | null => {
    return subCategories.find((sub) => sub.slug === subcategorySlug) || null;
  },

  /**
   * Validate both category and subcategory, throwing notFound() if invalid
   */
  validateCategoryAndSubcategory: (
    categorySlug: string,
    subcategorySlug: string | null,
    tabs: TabItem[],
  ) => {
    const category = validatePricingParams.validateCategory(categorySlug, tabs);
    if (!category) {
      notFound();
    }

    let subcategory: SubCategoryItem | null = null;
    if (subcategorySlug && category.subCategories) {
      subcategory = validatePricingParams.validateSubcategory(
        subcategorySlug,
        category.subCategories,
      );
      if (!subcategory) {
        notFound();
      }
    }

    return { category, subcategory };
  },
};

/**
 * Redirect utilities for pricing pages
 */
export const handlePricingRedirects = {
  /**
   * Handle redirect to first available category/subcategory when no category is selected
   * Only redirects on desktop devices, not on mobile
   */
  redirectToFirstCategory: (tabs: TabItem[], isMobile: boolean): void => {
    // Only redirect on desktop devices
    if (isMobile) {
      return;
    }

    if (tabs.length > 0 && tabs[0].subCategories && tabs[0].subCategories.length > 0) {
      const firstCategory = tabs[0];
      const firstSubCategory = firstCategory.subCategories?.[0];
      if (firstSubCategory) {
        redirect(`/pricing/${firstCategory.slug}/${firstSubCategory.slug}`);
      }
    }
  },
};

/**
 * Data fetching utilities for pricing pages
 */
export const fetchPricingData = {
  /**
   * Fetch product categories with error handling
   */
  getProductCategories: async (preview: boolean = false): Promise<TabItem[]> => {
    try {
      const categories = await fetchProductCategories(preview);
      return categories;
    } catch (error) {
      console.error('Error fetching product categories:', error);
      return [];
    }
  },

  /**
   * Check if pricing bands are enabled for a specific subcategory
   */
  isPricingBandEnabled: async (
    subcategorySlug: string,
    preview: boolean = false,
  ): Promise<boolean> => {
    try {
      const productCatalogue = await getProductCatalogue(subcategorySlug, preview);
      return productCatalogue?.pricingBandDisplayed || false;
    } catch (error) {
      console.error(`Error checking pricing band status for ${subcategorySlug}:`, error);
      return false;
    }
  },

  /**
   * Generate static params for all pricing routes
   */
  generateStaticParams: async (): Promise<{ slug: string[] }[]> => {
    try {
      const productCategories = await fetchProductCategories(false);
      const params: { slug: string[] }[] = [];

      // Generate params for each product category combination
      for (const category of productCategories) {
        // Add category-only route
        params.push({
          slug: [category.slug],
        });

        // Fetch subcategories for this category
        const subCategories = await getSubcategoriesByParent(category.slug, false);

        // Add category + subcategory routes if subcategories exist
        if (subCategories && subCategories.length > 0) {
          for (const subCategory of subCategories) {
            // Add subcategory-only route
            params.push({
              slug: [category.slug, subCategory.slug],
            });
          }
        }
      }

      return params;
    } catch (error) {
      console.error('Error generating static params for pricing:', error);
      return [];
    }
  },
};

export const processPricingParams = async (slug: string[] = [], preview: boolean = false) => {
  const [categorySlug, subcategorySlug] = slug;

  let tabs: TabItem[] = [];
  let subcategories: SubCategoryItem[] = [];

  let category: TabItem | null = null;
  let subcategory: SubCategoryItem | null = null;

  // Fetch all product categories
  tabs = await fetchPricingData.getProductCategories(preview);

  // Fetch subcategories for each tab and populate the subCategories property
  if (tabs.length > 0) {
    const tabsWithSubcategories = await Promise.all(
      tabs.map(async (tab) => {
        const tabSubcategories = await getSubcategoriesByParent(tab.slug, preview);
        return {
          ...tab,
          subCategories: tabSubcategories,
        };
      }),
    );
    tabs = tabsWithSubcategories;
  }

  if (categorySlug) {
    const validation = validatePricingParams.validateCategoryAndSubcategory(
      categorySlug,
      subcategorySlug,
      tabs,
    );
    category = validation.category;
    subcategory = validation.subcategory;
  }
  subcategories = category?.subCategories || [];

  return {
    tabs,
    category,
    subcategory,
    subcategories,
    categorySlug,
    subcategorySlug,
  };
};

/**
 * Contentful utilities for pricing pages
 */
export const contentfulPricingUtils = {
  /**
   * Fetch page data from Contentful
   */
  getPage: async (slug: string, preview: boolean = false) => {
    try {
      const page = await contentfulClient(
        preview,
      ).withoutUnresolvableLinks.getEntries<TypePageSkeleton>({
        content_type: 'page',
        'fields.slug': slug,
        include: 10,
      });

      if (!page || page.total === 0) {
        return null;
      }

      return page.items[0];
    } catch {
      return null;
    }
  },

  /**
   * Extract hero section data from page sections
   */
  getHeroSectionData: async (pageSlug: string, preview: boolean = false) => {
    const page = await contentfulPricingUtils.getPage(pageSlug, preview);

    if (!page) {
      return null;
    }

    // Extract hero section from page sections
    const heroSection = page.fields.sections?.find(
      (section) => section && isTypeHeroSection(section),
    ) as TypeHeroSection<'WITHOUT_UNRESOLVABLE_LINKS'> | undefined;

    if (!heroSection) {
      return null;
    }

    return {
      heading: heroSection.fields.heading,
      headingShort: heroSection.fields.headingShort,
      description: heroSection.fields.description,
      descriptionShort: heroSection.fields.descriptionShort,
      backgroundImage: heroSection.fields.background?.fields?.image?.fields?.file?.url,
    };
  },
};
